import { useState } from "react"
import type { DualRangeAnswer } from "@/types/agent"

import { Slider } from "@/components/ui/slider"

function calculateStep(min: number, max: number): number {
  const range = max - min
  if (range <= 10) return 1
  if (range <= 100) return 5
  if (range <= 500) return 10
  if (range <= 1000) return 25
  if (range <= 5000) return 50
  if (range <= 10000) return 100
  return 250
}

interface QuestionDualRangeInputProps {
  value: DualRangeAnswer | undefined
  onChange: (value: DualRangeAnswer) => void
  disabled?: boolean
  min?: number
  max?: number
  unit?: string
  defaultMin?: number
  defaultMax?: number
}

export function QuestionDualRangeInput({
  value,
  onChange,
  disabled,
  min = 0,
  max = 10000,
  unit = "",
  defaultMin,
  defaultMax,
}: QuestionDualRangeInputProps) {
  const step = calculateStep(min, max)
  const initialMin = value?.min ?? defaultMin ?? min + (max - min) * 0.25
  const initialMax = value?.max ?? defaultMax ?? min + (max - min) * 0.75

  const [minValue, setMinValue] = useState(initialMin)
  const [maxValue, setMaxValue] = useState(initialMax)

  const handleSliderChange = (sliderValue: number | readonly number[]) => {
    const values = Array.isArray(sliderValue) ? sliderValue : [sliderValue]
    const [newMin, newMax] = values
    if (newMax === undefined) return
    setMinValue(newMin)
    setMaxValue(newMax)
    onChange({ min: newMin, max: newMax })
  }

  const formatValue = (val: number) => {
    return unit ? `${val} ${unit}` : String(val)
  }

  return (
    <div className="space-y-2 py-2">
      <Slider
        value={[
          Math.min(max, Math.max(min, minValue)),
          Math.min(max, Math.max(min, maxValue)),
        ]}
        onValueChange={handleSliderChange}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
      />

      <div className="flex items-center justify-center gap-2 text-sm">
        <span className="font-medium text-gray-12">
          {formatValue(minValue)}
        </span>
        <span className="text-gray-10">—</span>
        <span className="font-medium text-gray-12">
          {formatValue(maxValue)}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-10">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  )
}
